import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Form,
  Input,
  List,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  Tabs,
} from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
import React, { useEffect, useState, useMemo } from "react";
import { SignatureCanvas } from "~/components/common/signature-canvas";
import { useNotification } from "~/hooks";
import { useCompleteServiceTicket } from "~/services/service-ticket";
import { useCompleteTicket } from "./complete-ticket.store";
import { useAuth } from "~/store";
import { Role } from "~/utils/constants";
import { useQueryClient } from "@tanstack/react-query";
import { capitalize, formatPackingType } from "~/utils";
import { useBulkCheckInventory } from "~/services/inventory";
import { SuppliesCheckList } from "~/components/staff/supplies/SuppliesCheckList";
import { TaskCheckList } from "~/components/staff/tasks";

const { Text, Title } = Typography;
const { TextArea } = Input;

interface CompleteServiceTicketModalProps {
  onClose?: () => void;
  onSuccess?: () => void;
}

interface FormValues {
  timeIn: dayjs.Dayjs | null;
  timeOut: dayjs.Dayjs | null;
  clientFirstName: string;
  jobLeadFirstName: string;
  staffCheckoff: boolean;
  completionNotes: string;
  adminOverride: boolean;
}

// Custom DateTimeSelect component for easier date and time selection
const DateTimeSelect: React.FC<{
  value?: dayjs.Dayjs | null;
  onChange?: (value: dayjs.Dayjs | null) => void;
  defaultDate?: dayjs.Dayjs;
  disabled?: boolean;
}> = ({ value, onChange, defaultDate, disabled }) => {
  // Helper function to round minutes to nearest 15-minute increment
  const roundToNearest15 = (time: dayjs.Dayjs) => {
    const minutes = time.minute();
    const roundedMinutes = Math.round(minutes / 15) * 15;
    return time.minute(roundedMinutes).second(0).millisecond(0);
  };

  // If no value, initialize with rounded current time on first interaction
  const currentDate = value || (defaultDate ? roundToNearest15(defaultDate) : roundToNearest15(dayjs()));

  // Generate hours (1-12)
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);

  // Generate minutes (00, 15, 30, 45)
  const minutes = [0, 15, 30, 45];

  // AM/PM options
  const periods = ["AM", "PM"];

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (!onChange || !date) return;

    const newDate = date.clone();
    newDate.hour(currentDate.hour());
    newDate.minute(currentDate.minute());
    newDate.second(0);
    newDate.millisecond(0);

    onChange(newDate);
  };

  const handleHourChange = (hour: number) => {
    if (!onChange) return;

    const newDate = currentDate.clone();
    // Convert 12-hour format to 24-hour format
    const isPM = newDate.hour() >= 12;
    const hour24 = hour === 12 ? (isPM ? 12 : 0) : isPM ? hour + 12 : hour;

    onChange(newDate.hour(hour24).second(0).millisecond(0));
  };

  const handleMinuteChange = (minute: number) => {
    if (!onChange) return;
    onChange(currentDate.clone().minute(minute).second(0).millisecond(0));
  };

  const handlePeriodChange = (period: string) => {
    if (!onChange) return;

    const newDate = currentDate.clone();
    const currentHour = newDate.hour() % 12;
    const newHour =
      period === "PM"
        ? currentHour === 0
          ? 12
          : currentHour + 12
        : currentHour;

    onChange(
      newDate.hour(
        period === "AM" ? (currentHour === 0 ? 0 : currentHour) : newHour
      ).second(0).millisecond(0)
    );
  };

  // Get current values
  const hour = currentDate.hour() % 12 || 12; // Convert 0 to 12 for 12-hour format
  const minute = currentDate.minute();
  const period = currentDate.hour() >= 12 ? "PM" : "AM";

  return (
    <div className="space-y-2">
      <DatePicker
        value={currentDate}
        onChange={handleDateChange}
        className="w-full"
        disabled={disabled}
        format="MM/DD/YYYY"
        // disabledDate={(current) => current < dayjs().startOf("day")}
      />
      <div className="flex space-x-2">
        <Select
          value={hour}
          onChange={handleHourChange}
          className="w-20"
          disabled={disabled}
        >
          {hours.map((h) => (
            <Select.Option key={h} value={h}>
              {h}
            </Select.Option>
          ))}
        </Select>
        <Select
          value={minute - (minute % 15)}
          onChange={handleMinuteChange}
          className="w-20"
          disabled={disabled}
        >
          {minutes.map((m) => (
            <Select.Option key={m} value={m}>
              {m.toString().padStart(2, "0")}
            </Select.Option>
          ))}
        </Select>
        <Select
          value={period}
          onChange={handlePeriodChange}
          className="w-20"
          disabled={disabled}
        >
          {periods.map((p) => (
            <Select.Option key={p} value={p}>
              {p}
            </Select.Option>
          ))}
        </Select>
      </div>
    </div>
  );
};

export const CompleteServiceTicketModal: React.FC<
  CompleteServiceTicketModalProps
> = ({ onClose, onSuccess }) => {
  const [form] = Form.useForm<FormValues>();

  const { user } = useAuth();

  const { isOpen, ticket, closeCompleteTicket } = useCompleteTicket();
  const [clientSignature, setClientSignature] = useState<string | null>(null);
  const [jobLeadSignature, setJobLeadSignature] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("tasks");
  const [allTasksChecked, setAllTasksChecked] = useState(false);
  const [completedTaskCount, setCompletedTaskCount] = useState(0);
  const [checkedInventoryCount, setCheckedInventoryCount] = useState(0);
  const [checkedInventories, setCheckedInventories] = useState<Record<string, boolean>>({});
  const [inventorySearchTerm, setInventorySearchTerm] = useState("");
  const [taskStatuses, setTaskStatuses] = useState<Record<string, 'pending' | 'completed' | 'removed'>>({});
  const [confirmedSupplies, setConfirmedSupplies] = useState<Record<string, { quantity: number; confirmed: boolean }>>({});

  // Watch form values for reactive updates
  const watchedTimeIn = Form.useWatch('timeIn', form);
  const watchedTimeOut = Form.useWatch('timeOut', form);

  const { success, error } = useNotification();
  const adminCompleteMutation = useCompleteServiceTicket();
  const bulkCheckInventoryMutation = useBulkCheckInventory();
  const queryClient = useQueryClient();

  const jobLead = ticket?.staffs?.find((staff) => staff.lead);
  const isAdmin = user?.role === Role.ADMIN || user?.role === Role.SUPER_ADMIN || user?.role === Role.SYSTEM_ADMIN;

  useEffect(() => {
    if (ticket && isOpen) {
      console.log('=== COMPLETION MODAL INITIALIZATION DEBUG ===');
      console.log('ticket.timeIn:', ticket.timeIn);
      console.log('ticket.timeOut:', ticket.timeOut);
      console.log('ticket.serviceDatetime (scheduled):', ticket.serviceDatetime);
      console.log('ticket.startDatetime (created):', ticket.startDatetime);

      // Helper function to round minutes to nearest 15-minute increment
      const roundToNearest15 = (time: dayjs.Dayjs) => {
        const minutes = time.minute();
        const roundedMinutes = Math.round(minutes / 15) * 15;
        return time.minute(roundedMinutes).second(0).millisecond(0);
      };

      // Auto-populate start time: use timeIn if available (when Start Service was clicked),
      // otherwise combine date from serviceDatetime with time from startDatetime
      let startTime: dayjs.Dayjs;
      if (ticket.timeIn) {
        // timeIn is stored as UTC in database, parse it in local timezone for display
        startTime = dayjs(ticket.timeIn);
      } else if (ticket.serviceDatetime && ticket.startDatetime) {
        // Combine: date from serviceDatetime + time from startDatetime (use local timezone, not UTC)
        const serviceDate = dayjs(ticket.serviceDatetime);
        const startTimeData = dayjs(ticket.startDatetime);
        const combined = serviceDate
          .hour(startTimeData.hour())
          .minute(startTimeData.minute())
          .second(startTimeData.second())
          .millisecond(startTimeData.millisecond());
        startTime = roundToNearest15(combined);
      } else if (ticket.serviceDatetime) {
        // Only serviceDatetime available
        startTime = roundToNearest15(dayjs(ticket.serviceDatetime));
      } else {
        // Current time as fallback - round to nearest 15 minutes
        startTime = roundToNearest15(dayjs());
      }

      let endTime: dayjs.Dayjs;
      if (ticket.timeOut) {
        // timeOut is stored as UTC in database, parse it in local timezone for display
        endTime = dayjs(ticket.timeOut);
      } else {
        // Default to current time (local timezone) - round to nearest 15 minutes
        endTime = roundToNearest15(dayjs());
      }

      console.log('Calculated startTime:', startTime.format('YYYY-MM-DD HH:mm:ss'));
      console.log('Calculated endTime:', endTime.format('YYYY-MM-DD HH:mm:ss'));

      // Pre-populate with existing data if available
      form.setFieldsValue({
        timeIn: startTime,
        timeOut: endTime,
        clientFirstName: ticket.clientFirstName || "",
        jobLeadFirstName: ticket.jobLeadFirstName || "",
        staffCheckoff: false,
        completionNotes: "",
        adminOverride: false,
      });

      // Set existing signatures if available
      setClientSignature(ticket.clientSignature || null);
      setJobLeadSignature(ticket.jobLeadSignature || null);

      // if no tasks, set all tasks checked
      if (!ticket.addOnServices || ticket.addOnServices.length === 0) {
        setAllTasksChecked(true);
      }
      // Reset tasks checked state
      else setAllTasksChecked(false);
    }
  }, [ticket, isOpen, form]);

  const handleTasksChecked = (_taskIds: string[], allChecked: boolean, completedCount?: number, statuses?: Record<string, 'pending' | 'completed' | 'removed'>) => {
    setAllTasksChecked(allChecked);
    if (completedCount !== undefined) {
      setCompletedTaskCount(completedCount);
    }
    if (statuses) {
      setTaskStatuses(statuses);
    }
  };

  const handleSuppliesChecked = (_supplyIds: string[], _checked: boolean, confirmedSuppliesData?: Record<string, { quantity: number; confirmed: boolean }>) => {
    if (confirmedSuppliesData) {
      setConfirmedSupplies(confirmedSuppliesData);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!ticket || !user) return;

      // Validate required fields
      if (!values.timeIn || !values.timeOut) {
        error({ message: "Please confirm both time in and time out" });
        return;
      }

      // Validate duration - block negative durations
      const startUTC = dayjs.utc(values.timeIn.format('YYYY-MM-DDTHH:mm:ss'));
      const endUTC = dayjs.utc(values.timeOut.format('YYYY-MM-DDTHH:mm:ss'));
      const durationMins = Math.round(endUTC.diff(startUTC, 'minute', true));
      if (durationMins < 0) {
        error({
          message: "Time Out is before Time In",
          description: "The end time cannot be before the start time. Please check the date, time, and AM/PM selections.",
        });
        return;
      }

      if (!clientSignature) {
        error({ message: "Client signature is required" });
        return;
      }

      if (!values.clientFirstName.trim()) {
        error({ message: "Client first name is required" });
        return;
      }

      // Check if job lead signature is required (if there's a job lead)
      if (!jobLeadSignature) {
        error({ message: "Job lead signature is required" });
        return;
      }

      if (!values.jobLeadFirstName?.trim()) {
        error({ message: "Job lead first name is required" });
        return;
      }

      // Validate based on user role
      const isAdmin =
        user.role === Role.ADMIN || user.role === Role.SUPER_ADMIN || user.role === Role.SYSTEM_ADMIN;

      if (!isAdmin && !values.staffCheckoff) {
        error({ message: "Staff checkoff confirmation is required" });
        return;
      }

      if (isAdmin && !values.adminOverride) {
        error({ message: "Admin checkoff confirmation is required" });
        return;
      }

      // Check if there are tasks that need to be completed
      if (
        ticket.addOnServices &&
        ticket.addOnServices.length > 0 &&
        !allTasksChecked
      ) {
        error({
          message: "All required tasks must be completed",
          description:
            "Please go to the Required Tasks tab and check off all tasks before completing the service ticket.",
        });
        setActiveTab("tasks");
        return;
      }

      // Debug logging
      console.log('=== COMPLETION SUBMISSION DEBUG ===');
      console.log('timeIn from form (local):', values.timeIn.format('YYYY-MM-DD HH:mm:ss'));
      console.log('timeOut from form (local):', values.timeOut.format('YYYY-MM-DD HH:mm:ss'));

      // Format as ISO but treat as UTC to preserve the local time values
      // This prevents timezone conversion - 7:00 PM stays as 7:00 PM in database
      const timeInISO = values.timeIn.format('YYYY-MM-DDTHH:mm:ss.000') + 'Z';
      const timeOutISO = values.timeOut.format('YYYY-MM-DDTHH:mm:ss.000') + 'Z';
      const completedAtISO = dayjs().format('YYYY-MM-DDTHH:mm:ss.000') + 'Z';

      console.log('timeIn ISO (preserved local):', timeInISO);
      console.log('timeOut ISO (preserved local):', timeOutISO);
      console.log('completedAt ISO (preserved local):', completedAtISO);

      // Build supply items array with confirmed status
      const supplyItems = ticket.supplyItems?.map((item) => {
        const supplyId = item.supplyId || '';
        const supplyItem: any = {
          supplyId: supplyId,
          quantity: confirmedSupplies[supplyId]?.quantity || item.quantity,
          confirmed: confirmedSupplies[supplyId]?.confirmed || false,
        };

        // Only include unitCost if it's a valid number
        if (item.unitCost != null && !isNaN(Number(item.unitCost))) {
          supplyItem.unitCost = Number(item.unitCost);
        }

        return supplyItem;
      }) || [];

      // Submit completion
      const payload = {
        id: ticket.id,
        timeIn: timeInISO,
        timeOut: timeOutISO,
        completedAt: completedAtISO,
        clientSignature: clientSignature,
        clientFirstName: values.clientFirstName.trim(),
        jobLeadSignature: jobLeadSignature || undefined,
        jobLeadFirstName: values.jobLeadFirstName.trim() || undefined,
        staffCheckoff: isAdmin ? false : values.staffCheckoff,
        completionNotes: values.completionNotes,
        adminOverride: isAdmin ? values.adminOverride : false,
        taskStatuses: Object.keys(taskStatuses).map(taskId => ({
          taskId,
          status: taskStatuses[taskId],
        })),
        supplyItems: supplyItems.length > 0 ? supplyItems : undefined,
      };

      console.log('Full payload:', JSON.stringify(payload, null, 2));
      console.log('Task statuses being sent:', payload.taskStatuses);
      console.log('Supply items being sent:', payload.supplyItems);

      // Save checked inventory items before completing the ticket
      const confirmedInventoryIds = Object.entries(checkedInventories)
        .filter(([_, isChecked]) => isChecked)
        .map(([id]) => id);

      if (confirmedInventoryIds.length > 0) {
        console.log('Confirming inventory items:', confirmedInventoryIds);
        await bulkCheckInventoryMutation.mutateAsync({
          inventoryIds: confirmedInventoryIds,
          isChecked: true,
          serviceTicketId: ticket.id,
        });
      }

      await adminCompleteMutation.mutateAsync(payload);

      success({ message: "Service ticket completed successfully" });

      // Additional cache invalidation for immediate UI updates
      queryClient.invalidateQueries();

      handleClose();
      onSuccess?.();
    } catch (err: any) {
      console.error("Error completing service ticket:", err);
      console.error("Error response:", err?.response);
      console.error("Error data:", err?.response?.data);
      console.error("Error message:", err?.response?.data?.message || err?.message);

      const errorMessage = err?.response?.data?.message || err?.message || "Unknown error";
      const errorDetails = Array.isArray(err?.response?.data?.message)
        ? err.response.data.message.join(", ")
        : errorMessage;

      error({
        message: "Failed to complete service ticket",
        description: errorDetails || "Please try again or contact support",
      });
    }
  };

  const handleClose = () => {
    form.resetFields();
    setClientSignature(null);
    setJobLeadSignature(null);
    setAllTasksChecked(false);
    setCompletedTaskCount(0);
    setCheckedInventoryCount(0);
    setCheckedInventories({});
    setInventorySearchTerm("");
    closeCompleteTicket();
    onClose?.();
  };

  // Calculate service duration and stats for summary with reactive updates
  const serviceSummary = useMemo(() => {
    let duration = "Not calculated";
    if (watchedTimeIn && watchedTimeOut) {
      // Convert both to UTC mode to ensure consistent calculation
      const startUTC = dayjs.utc(watchedTimeIn.format('YYYY-MM-DDTHH:mm:ss'));
      const endUTC = dayjs.utc(watchedTimeOut.format('YYYY-MM-DDTHH:mm:ss'));

      console.log('Duration calc - startUTC:', startUTC.format('YYYY-MM-DD HH:mm:ss'));
      console.log('Duration calc - endUTC:', endUTC.format('YYYY-MM-DD HH:mm:ss'));

      const exactMinutes = endUTC.diff(startUTC, 'minute', true);
      console.log('Duration calc - exactMinutes:', exactMinutes);

      // Round to nearest minute for display
      const totalMinutes = Math.round(exactMinutes);

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (totalMinutes < 0) {
        duration = "Invalid time range";
      } else if (totalMinutes === 0) {
        duration = "0m";
      } else if (hours > 0) {
        duration = minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
      } else {
        duration = `${minutes}m`;
      }
    }

    // Count only completed tasks (not pending or removed from scope)
    // Now connected to the actual task status from TaskCheckList
    const completedTasks = completedTaskCount;

    // Calculate raw total minutes for validation
    let durationMinutes: number | null = null;
    if (watchedTimeIn && watchedTimeOut) {
      const startUTC2 = dayjs.utc(watchedTimeIn.format('YYYY-MM-DDTHH:mm:ss'));
      const endUTC2 = dayjs.utc(watchedTimeOut.format('YYYY-MM-DDTHH:mm:ss'));
      durationMinutes = Math.round(endUTC2.diff(startUTC2, 'minute', true));
    }

    return {
      duration,
      durationMinutes,
      checkedInventoryCount,
      totalInventory: ticket?.inventories?.length || 0,
      completedTasks,
      totalTasks: ticket?.addOnServices?.length || 0
    };
  }, [watchedTimeIn, watchedTimeOut, completedTaskCount, checkedInventoryCount, ticket?.inventories?.length, ticket?.addOnServices?.length]);

  if (!ticket) return null;

  return (
    <Modal
      title={
        <div className="border-b border-gray-200 pb-4 -mx-6 px-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">✓</span>
            </div>
            <div>
              <Title level={4} className="mb-1 text-gray-800">
                Complete Service Ticket
              </Title>
              <Text type="secondary" className="text-sm">
                {ticket.serial} - {capitalize(ticket.serviceType)}
              </Text>
            </div>
          </div>
        </div>
      }
      open={isOpen}
      onCancel={handleClose}
      width={900}
      className="complete-service-modal"
      footer={
        <div className="flex justify-between items-center w-full">
          <Button onClick={handleClose}>Cancel</Button>

          <div className="text-sm text-gray-500">
            {(() => {
              const tabs = ["tasks", "inventory", "supplies", "summary"];
              const currentIndex = tabs.indexOf(activeTab);
              return `Step ${currentIndex + 1} of ${tabs.length}`;
            })()}
          </div>

          <Space>
            {(() => {
              const tabs = ["tasks", "inventory", "supplies", "summary"];
              const currentIndex = tabs.indexOf(activeTab);
              return currentIndex > 0 ? (
                <Button
                  onClick={() => {
                    setActiveTab(tabs[currentIndex - 1]);
                  }}
                >
                  Back
                </Button>
              ) : null;
            })()}

            {activeTab === "summary" ? (
              <div className="flex items-center gap-3">
                {(() => {
                  const missing: string[] = [];
                  if (!allTasksChecked) missing.push("Complete all tasks");
                  if (!clientSignature) missing.push("Client signature");
                  if (!jobLeadSignature) missing.push("Job lead signature");

                  if (missing.length > 0) {
                    return (
                      <div className="text-xs text-orange-600 bg-orange-50 px-3 py-1 rounded-lg border border-orange-200">
                        🔒 {missing.join(" • ")}
                      </div>
                    );
                  }
                  return null;
                })()}
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  loading={adminCompleteMutation.isPending}
                  disabled={!allTasksChecked || !clientSignature || !jobLeadSignature}
                >
                  Complete Service
                </Button>
              </div>
            ) : (
              <Button
                type="primary"
                onClick={() => {
                  const tabs = ["tasks", "inventory", "supplies", "summary"];
                  const currentIndex = tabs.indexOf(activeTab);
                  if (currentIndex < tabs.length - 1) {
                    setActiveTab(tabs[currentIndex + 1]);
                  }
                }}
              >
                Next
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Always show tabs version with all sections for both admin and staff */}
        <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className="complete-ticket-tabs"
            size="large"
            items={[
              {
                key: "tasks",
                label: "Time + Tasks",
                children: (
                  <Form form={form} layout="vertical">
                    <div className="mt-4 space-y-4">
                      {/* Time Confirmation - Now part of Required Tasks */}
                      <Card
                        title={
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-blue-600 font-semibold text-sm">1</span>
                            </div>
                            <span className="text-lg font-semibold text-gray-800">Service Time Confirmation</span>
                          </div>
                        }
                        className="shadow-sm border border-gray-200"
                      >
                        <div className="bg-blue-50 p-3 rounded-lg mb-4">
                          <Text type="secondary" className="text-sm">
                            ⏰ Confirm the service start and end times. This is required for accurate billing.
                          </Text>
                        </div>

                        <div className="text-xs text-gray-600 mb-3">
                          Start time is automatically set to when you clicked "Start Service" (if available),
                          or defaults to the scheduled service date/time. End time defaults to current time.
                          Adjust date and time using the selectors below if needed.
                        </div>

                        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                          <Form.Item
                            name="timeIn"
                            label="Time In"
                            rules={[
                              { required: true, message: "Time in is required" },
                            ]}
                          >
                            <DateTimeSelect
                              defaultDate={
                                ticket.serviceDatetime && ticket.startDatetime
                                  ? (() => {
                                      // Combine date from serviceDatetime with time from startDatetime
                                      const serviceDate = dayjs(ticket.serviceDatetime);
                                      const startTime = dayjs(ticket.startDatetime);
                                      return serviceDate
                                        .hour(startTime.hour())
                                        .minute(startTime.minute())
                                        .second(startTime.second());
                                    })()
                                  : ticket.serviceDatetime
                                  ? dayjs(ticket.serviceDatetime)
                                  : undefined
                              }
                            />
                          </Form.Item>
                          <Form.Item
                            name="timeOut"
                            label="Time Out"
                            rules={[
                              { required: true, message: "Time out is required" },
                            ]}
                          >
                            <DateTimeSelect defaultDate={dayjs()} />
                          </Form.Item>
                          {serviceSummary.durationMinutes !== null && serviceSummary.durationMinutes < 0 && (
                            <Alert
                              type="error"
                              showIcon
                              message="Time Out is before Time In"
                              description="Check the date, time, and AM/PM selections. This ticket cannot be completed until corrected."
                            />
                          )}
                          {serviceSummary.durationMinutes !== null && serviceSummary.durationMinutes > 480 && (
                            <Alert
                              type="warning"
                              showIcon
                              message={`Duration exceeds 8 hours (${serviceSummary.duration})`}
                              description="Please double-check the date, time, and AM/PM selections are correct."
                            />
                          )}
                        </div>
                      </Card>

                      {/* Required Tasks */}
                      <Card
                        title={
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-blue-600 font-semibold text-sm">2</span>
                            </div>
                            <span className="text-lg font-semibold text-gray-800">Required Tasks</span>
                          </div>
                        }
                        className="shadow-sm border border-gray-200"
                      >
                        <div className="bg-blue-50 p-3 rounded-lg mb-4">
                          <Text type="secondary" className="text-sm">
                            ✅ All tasks must be checked off before the service ticket can be completed.
                          </Text>
                        </div>
                        <TaskCheckList
                          serviceTicket={ticket}
                          onTasksChecked={handleTasksChecked}
                        />
                      </Card>
                    </div>
                  </Form>
                ),
              },
              {
                key: "inventory",
                label: "Inventory Check",
                children: (
                  <div className="mt-4">
                    <Card
                      title={
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-semibold text-gray-800">Inventory Check</span>
                        </div>
                      }
                      className="shadow-sm border border-gray-200"
                    >
                      <div className="bg-green-50 p-3 rounded-lg mb-4">
                        <Text type="secondary" className="text-sm">
                          📦 Select only inventory items that have been collected or delivered during service.
                        </Text>
                      </div>
                      {ticket.inventories && ticket.inventories.length > 0 ? (
                        <div>
                          {/* Summary Bar */}
                          <div className="bg-gray-50 p-3 rounded-lg border mb-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="flex items-center px-4 py-2 bg-green-50 border-2 border-green-500 rounded-lg">
                                  <span className="font-bold text-green-700 text-base">Confirmed:</span>
                                  <span className="ml-2 px-4 py-1 bg-green-500 text-white rounded-full text-lg font-bold">
                                    {checkedInventoryCount}
                                  </span>
                                </div>
                                <div className="flex items-center">
                                  <span className="font-medium text-gray-600 text-sm">Total:</span>
                                  <span className="ml-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                                    {ticket.inventories.length}
                                  </span>
                                </div>
                                <div className="flex items-center">
                                  <span className="font-medium text-red-600 text-sm">Not Confirmed:</span>
                                  <span className="ml-1 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                                    {ticket.inventories.length - checkedInventoryCount}
                                  </span>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="small"
                                  onClick={() => {
                                    const allChecked: Record<string, boolean> = {};
                                    ticket.inventories?.forEach((inv) => {
                                      allChecked[inv.id] = true;
                                    });
                                    setCheckedInventories(allChecked);
                                    setCheckedInventoryCount(ticket.inventories?.length || 0);
                                  }}
                                >
                                  Select All
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setCheckedInventories({});
                                    setCheckedInventoryCount(0);
                                  }}
                                >
                                  Clear All
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Search Bar */}
                          <div className="mb-4">
                            <Input
                              placeholder="Search by title, artist, AHC ref, client ref, or dimensions..."
                              value={inventorySearchTerm}
                              onChange={(e) => setInventorySearchTerm(e.target.value)}
                              allowClear
                              prefix={<span className="text-gray-400">🔍</span>}
                              className="w-full"
                            />
                          </div>

                          {/* Inventory List */}
                          <List
                            itemLayout="horizontal"
                            dataSource={ticket.inventories.filter((item) => {
                              if (!inventorySearchTerm.trim()) return true;
                              const search = inventorySearchTerm.toLowerCase();
                              const dimensions = `${item.length || 0}" × ${item.width || 0}" × ${item.height || 0}"`;
                              return (
                                item.title?.toLowerCase().includes(search) ||
                                item.artistName?.toLowerCase().includes(search) ||
                                item.inventoryRef?.toLowerCase().includes(search) ||
                                item.clientInternalRef?.toLowerCase().includes(search) ||
                                dimensions.toLowerCase().includes(search) ||
                                item.packingType?.toLowerCase().includes(search) ||
                                item.status?.toLowerCase().includes(search)
                              );
                            })}
                            renderItem={(item) => (
                              <List.Item
                                className="py-3 px-4 border rounded mb-2 hover:bg-gray-50 cursor-pointer"
                                onClick={() => {
                                  const newChecked = !checkedInventories[item.id];
                                  setCheckedInventories((prev) => ({
                                    ...prev,
                                    [item.id]: newChecked,
                                  }));
                                  setCheckedInventoryCount((prev) =>
                                    newChecked ? prev + 1 : prev - 1
                                  );
                                }}
                              >
                                <div className="mr-4" onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    checked={checkedInventories[item.id] || false}
                                    onChange={(e) => {
                                      const newChecked = e.target.checked;
                                      setCheckedInventories((prev) => ({
                                        ...prev,
                                        [item.id]: newChecked,
                                      }));
                                      setCheckedInventoryCount((prev) =>
                                        newChecked ? prev + 1 : prev - 1
                                      );
                                    }}
                                  />
                                </div>
                                <List.Item.Meta
                                  title={
                                    <div className="flex items-center flex-wrap gap-2">
                                      <span className="font-medium">
                                        {item.title || "Untitled"}
                                      </span>
                                      <Tag color="blue">{item.inventoryRef}</Tag>
                                      <Tag>{capitalize(item.status)}</Tag>
                                    </div>
                                  }
                                  description={
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mt-1">
                                      <div>
                                        <Text type="secondary" className="text-xs">Artist:</Text>
                                        <div>{item.artistName || "N/A"}</div>
                                      </div>
                                      <div>
                                        <Text type="secondary" className="text-xs">Client Ref:</Text>
                                        <div>{item.clientInternalRef || "N/A"}</div>
                                      </div>
                                      <div>
                                        <Text type="secondary" className="text-xs">Dimensions:</Text>
                                        <div>{`${item.length || 0}" × ${item.width || 0}" × ${item.height || 0}"`}</div>
                                      </div>
                                      <div>
                                        <Text type="secondary" className="text-xs">Packing:</Text>
                                        <div>{formatPackingType(item)}</div>
                                      </div>
                                    </div>
                                  }
                                />
                              </List.Item>
                            )}
                          />
                        </div>
                      ) : (
                        <div className="text-center py-8 bg-gray-50 rounded-lg">
                          <Text type="secondary">
                            No inventory items found for this service ticket.
                          </Text>
                        </div>
                      )}
                    </Card>
                  </div>
                ),
              },
              {
                key: "supplies",
                label: "Supplies",
                children: (
                  <div className="mt-4">
                    <Card
                      title={
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                            <span className="text-orange-600 font-semibold text-sm">4</span>
                          </div>
                          <span className="text-lg font-semibold text-gray-800">Supplies</span>
                        </div>
                      }
                      className="shadow-sm border border-gray-200"
                    >
                      <div className="bg-orange-50 p-3 rounded-lg mb-4">
                        <Text type="secondary" className="text-sm">
                          🛠️ Verify supplies used for this service ticket. Adjust quantities as needed.
                        </Text>
                      </div>
                      <SuppliesCheckList ticket={ticket} onSuppliesChecked={handleSuppliesChecked} />
                    </Card>
                  </div>
                ),
              },
              {
                key: "summary",
                label: "Summary + Sign Off",
                children: (
                  <Form form={form} layout="vertical">
                    <div className="mt-4">
                      <Card
                        title={
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                              <span className="text-purple-600 font-semibold text-sm">5</span>
                            </div>
                            <span className="text-lg font-semibold text-gray-800">Service Summary + Sign Off</span>
                          </div>
                        }
                        className="shadow-sm border border-gray-200 mb-6"
                      >
                        <div className="bg-purple-50 p-3 rounded-lg mb-4">
                          <Text type="secondary" className="text-sm">
                            ✍️ Final step: Review service summary, collect signatures, and complete the service ticket.
                          </Text>
                        </div>

                        {/* Service Summary */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                          <h4 className="text-md font-semibold text-gray-800 mb-3">📋 Service Summary</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div className="space-y-3">
                              <div className="flex justify-between">
                                <Text type="secondary">Service Type:</Text>
                                <Text strong>{capitalize(ticket.serviceType)}</Text>
                              </div>
                              <div className="flex justify-between">
                                <Text type="secondary">Service Client:</Text>
                                <Text strong>
                                  {ticket.client?.companyName ||
                                   `${ticket.client?.firstName} ${ticket.client?.lastName}` ||
                                   'Not specified'}
                                </Text>
                              </div>
                              <div className="flex justify-between">
                                <Text type="secondary">Service Location:</Text>
                                <Text strong className="text-right max-w-48 truncate">
                                  {ticket.location?.address || 'Not specified'}
                                </Text>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <div className="flex justify-between">
                                <Text type="secondary">Total Duration:</Text>
                                <Text strong>{serviceSummary.duration}</Text>
                              </div>
                              <div className="flex justify-between">
                                <Text type="secondary">Inventory Items:</Text>
                                <Text strong>
                                  {serviceSummary.checkedInventoryCount} of {serviceSummary.totalInventory} confirmed
                                </Text>
                              </div>
                              <div className="flex justify-between">
                                <Text type="secondary">Required Tasks:</Text>
                                <Text strong>
                                  {serviceSummary.completedTasks} of {serviceSummary.totalTasks} completed
                                </Text>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Client Information and Signature */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                          <h4 className="text-md font-semibold text-gray-800 mb-3">👤 Client Information & Signature</h4>
                          <Form.Item
                            name="clientFirstName"
                            label="Client First Name"
                            rules={[
                              {
                                required: true,
                                message: "Client first name is required",
                              },
                            ]}
                            className="mb-4"
                          >
                            <Input placeholder="Enter client's first name" />
                          </Form.Item>

                          <div className="text-center">
                            <SignatureCanvas
                              onSignatureChange={setClientSignature}
                              width={
                                window.innerWidth < 768
                                  ? window.innerWidth - 100
                                  : 700
                              }
                              height={window.innerWidth < 768 ? 180 : 200}
                              className="mx-auto w-full max-w-full"
                            />
                            <Text type="secondary" className="text-sm mt-2 block">
                              Client signature is required to complete the service
                            </Text>
                            <Text
                              type="secondary"
                              className="text-xs mt-1 block text-gray-400"
                            >
                              {window.innerWidth < 768
                                ? "Use your finger to sign on mobile/tablet"
                                : "Click and drag to create signature"}
                            </Text>
                          </div>
                        </div>

                        {/* Job Lead Information and Signature */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                          <h4 className="text-md font-semibold text-gray-800 mb-3">👨‍💼 Job Lead Sign-off</h4>
                          <Form.Item
                            name="jobLeadFirstName"
                            label="Job Lead First Name"
                            rules={[
                              {
                                required: true,
                                message: "Job lead first name is required",
                              },
                            ]}
                            className="mb-4"
                          >
                            <Input placeholder="Enter job lead's first name" />
                          </Form.Item>

                          <div className="text-center">
                            <SignatureCanvas
                              onSignatureChange={setJobLeadSignature}
                              width={
                                window.innerWidth < 768
                                  ? window.innerWidth - 100
                                  : 700
                              }
                              height={window.innerWidth < 768 ? 150 : 180}
                              className="mx-auto w-full max-w-full"
                            />
                            <Text type="secondary" className="text-sm mt-2 block">
                              Job lead signature is required to authorize service
                              completion
                            </Text>
                            <Text
                              type="secondary"
                              className="text-xs mt-1 block text-gray-400"
                            >
                              Lead: {jobLead?.user?.firstName}{" "}
                              {jobLead?.user?.lastName}
                            </Text>
                          </div>
                        </div>

                        {/* Completion Notes */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                          <h4 className="text-md font-semibold text-gray-800 mb-3">
                            📝 Completion Notes{' '}
                            <span className="text-xs font-normal italic text-gray-500">
                              These notes will be viewable by all parties within the waybill.
                            </span>
                          </h4>
                          <Form.Item
                            name="completionNotes"
                            label="Additional Notes"
                            extra="Add any additional notes about the service completion"
                          >
                            <TextArea
                              rows={4}
                              placeholder="Enter any additional notes about the service completion..."
                            />
                          </Form.Item>
                        </div>

                        {/* Completion Confirmation */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                          <h4 className="text-md font-semibold text-gray-800 mb-3">✅ Completion Confirmation</h4>
                          <Form.Item
                            name={isAdmin ? "adminOverride" : "staffCheckoff"}
                            valuePropName="checked"
                            rules={[
                              {
                                validator: (_, value) =>
                                  value
                                    ? Promise.resolve()
                                    : Promise.reject("Confirmation is required"),
                              },
                            ]}
                          >
                            <Checkbox>
                              {isAdmin
                                ? "I confirm that all service requirements have been completed and authorize completion of this service ticket"
                                : "I confirm that all service requirements have been completed and all staff have checked off their tasks"}
                            </Checkbox>
                          </Form.Item>
                        </div>
                      </Card>
                    </div>
                  </Form>
                ),
              },
            ]}
          />
      </div>
    </Modal>
  );
};
